import { db } from '@app/icare/client/db'
import {
  localPaginatedQueryOptions,
  type ResultTypeFromQueryResult,
} from '@app/icare/client/db/client/localQueryOptions'
import { patient } from '@app/icare/client/db/helpers'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import {
  BiaMeasurementFormStatusEnum,
  type BiaMeasurementFormStatusType,
} from '@app/icare/db/schema/form/BiaMeasurement'
import { PatientFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import {
  type PatientIdType,
  type PatientStatus,
  PatientStatusEnum,
  type PatientStatusType,
} from '@app/icare/entities/patient/schema/Patient'
import ActiveEditors from '@app/icare/entities/user/components/ActiveEditors'
import { UserInfo } from '@app/icare/entities/user/components/UserInfo'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { formatDate, formatDateTime } from '@pkg/shared/utils'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Descriptions, Space, Table } from 'antd'
import { sql } from 'kysely'
import { useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/bia-measurements')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z
    .enum([
      'patientForm.createdAt',
      'patientForm.createdBy',
      'patientForm.updatedBy',
      'patient.personal.lastName',
      'patientForm.status',
    ])
    .default('patientForm.createdAt')
    .nullish(),
  filters: z
    .object({
      status: Q.in(z.enum(BiaMeasurementFormStatusEnum.$.zodEnum())).nullish(),
    })
    .optional(),
})

const PatientStatusValueSchema = z.enum(PatientStatusEnum.$.zodEnum())
const INITIAL_FILTERS = {
  'patientForm.status': F.in([BiaMeasurementFormStatusEnum.new, BiaMeasurementFormStatusEnum.processing]),
  'patient.status': F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
}
const useBiaQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
  },
})

function RouteComponent() {
  const queryStore = useBiaQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const biaParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const patientStatusFilter = biaParams.filters.find(f => f.field === 'patient.status')
  const patientStatusValues: PatientStatusType[] =
    patientStatusFilter?.operator === 'in' && Array.isArray(patientStatusFilter.value)
      ? patientStatusFilter.value.flatMap(value => {
          const parsed = PatientStatusValueSchema.safeParse(value)
          return parsed.success ? [parsed.data] : []
        })
      : patientStatusFilter?.operator === 'eq'
        ? (() => {
            const parsed = PatientStatusValueSchema.safeParse(patientStatusFilter.value)
            return parsed.success ? [parsed.data] : []
          })()
        : []
  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll('patientForm')
        .select(patient.select('patientForm.patientId', ['id', 'personal', 'contact', 'status', 'address']))
        .where('patientForm.type', '=', PatientFormTypeEnum.BiaMeasurement)
        .$call(queryKit.filters(biaParams, ['patientForm.status', 'status']))
        .$call(queryKit.search(biaParams))

        .$if(patientStatusValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patient')
                .select('patient.id')
                .whereRef('patient.id', '=', 'patientForm.patientId')
                .where(eb => eb(eb.ref('patient.status', '->>').key('value'), 'in', patientStatusValues)),
            ),
          ),
        )
        .$call(queryKit.deleted(biaParams.deleted, db.dynamic.ref('patientForm.deletedAt')))
        .$if(biaParams.sortBy === 'patient.personal.lastName', qb =>
          qb.orderBy(
            sql`(
              select json_extract("patient"."personal", '$.lastName')
              from "patient"
              where "patient"."id" = "patientForm"."patientId"
            )`,
            biaParams.sortDir,
          ),
        )
        .$if(
          biaParams.sortBy !== 'patientForm.form.supply' &&
            biaParams.sortBy !== 'patientForm.form.deliveryTime.from' &&
            biaParams.sortBy !== 'patient.personal.lastName',
          qb => qb.$call(queryKit.sort(biaParams)),
        )
        .$call(queryKit.paginate(biaParams)),
      params: biaParams,
    }),
  )

  type BiaRecord = ResultTypeFromQueryResult<typeof data>

  const columns = useColumnDef<BiaRecord>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) =>
          record.patient && (
            <FormattedPatientNameAndAddress
              patient={{
                ...record.patient,
                id: record.patient.id as PatientIdType,
                status: record.patient.status as PatientStatus,
              }}
            />
          ),
      },
      {
        key: 'patientForm.status',
        title: t`Status`,
        sorter: true,
        render: (_, record) => (
          <FormStatusBadge<BiaMeasurementFormStatusType>
            value={record.status as BiaMeasurementFormStatusType}
            statusEnum={BiaMeasurementFormStatusEnum}
          />
        ),
      },
      {
        key: 'patientForm.createdAt',
        title: <Trans>Erstellt am</Trans>,
        sorter: true,
        render: (_, record) => formatDate(record.createdAt),
      },
      {
        key: 'patientForm.createdBy',
        title: <Trans>Erstellt von</Trans>,
        sorter: true,
        render: (_, record) => <UserInfo userId={record.createdBy} />,
      },
      {
        key: 'usersEditing',
        title: <Trans>aktuelle Bearbeitende</Trans>,
        sorter: false,
        render: (_, record) => (
          <ActiveEditors
            routeId="/_app/patienten/$id/_edit/_documents/_forms/bia-messung/$form/"
            params={{ id: record.patientId, form: record.id }}
          />
        ),
      },
      {
        key: 'patientForm.id',
        title: t`Aktionen`,
        fixed: 'end',
        sorter: false,
        render: (_, record) => (
          <ButtonLink
            to="/patienten/$id/bia-messung/$form"
            params={{ id: record.patientId, form: record.id }}
            size="small"
          >
            <Trans>zum Dokument</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    useBiaQueryStore,
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )
  return (
    <QueryFilterWrapper
      queryStore={useBiaQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status',
          mode: 'multiple',
          label: <Trans>Status der Patitentenakte</Trans>,
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'patientForm.status',
          mode: 'multiple',
          label: <Trans>Status der BIA-Messung</Trans>,
          options: BiaMeasurementFormStatusEnum.$.options,
          direction: 'horizontal',
        },
      ]}
    >
      <Table<BiaRecord>
        dataSource={data?.data ?? []}
        loading={isFetching}
        rowKey="id"
        size="small"
        columns={columns}
        pagination={data}
        onChange={queryStore.onChange}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: record => (
            <Descriptions
              column={1}
              size="small"
              style={{ paddingLeft: 32 }}
              items={[
                {
                  label: t`Erstellt von`,
                  key: 'createdBy',
                  children: <UserInfo userId={record.createdBy} />,
                },
                {
                  label: t`Erstellt am`,
                  key: 'createdAt',
                  children: formatDateTime(record.createdAt),
                },
                {
                  label: t`Geändert von`,
                  key: 'updatedBy',
                  children: <UserInfo userId={record.updatedBy} />,
                },
                {
                  label: t`Geändert am`,
                  key: 'updatedAt',
                  children: formatDateTime(record.updatedAt),
                },
              ]}
            />
          ),
        }}
      />
    </QueryFilterWrapper>
  )
}
